/**
 * The font size a label should use to fit its box.
 *
 * Steps down from `maxPx` by `stepPx` until `fits(size)` says the text fits on
 * one line, but never below `minPx` — a name shrunk to 7px is not readable,
 * and that is worse than a taller row. When even `minPx` does not fit, the
 * answer is `minPx` with `wrap: true`: the label goes to two lines and the row
 * grows to hold it.
 *
 * `fits` is the only thing that touches the DOM (set a size, compare
 * scrollWidth with clientWidth), which keeps the decision itself testable.
 */
export const fitFontSize = (
  fits: (px: number) => boolean,
  maxPx: number,
  minPx: number,
  stepPx = 0.5
): {size: number; wrap: boolean} => {
  for (let size = maxPx; size >= minPx - 1e-9; size -= stepPx) {
    const rounded = Math.round(size * 100) / 100
    if (fits(rounded)) return {size: rounded, wrap: false}
  }
  return {size: minPx, wrap: true}
}

/** The smallest a label may shrink to, relative to its normal size. */
export const MIN_FONT_RATIO = 0.8
